import { Constants } from "@eagerdog/constants";
import { IEventRegisterRow } from "../../../services/helper.service.js";
import { IShow } from "@eagerdog/interfaces";

export namespace AKCConformation {
  export const getForm = (show?: IShow): IEventRegisterRow[] => {
    // Helper function to check if an element/level is available in the show
    const isElementAvailable = (elementOrLevel: string): boolean => {
      if (!show?.show_elements) return true; // If no show provided, show all options

      const result = show.show_elements.some(
        (se) =>
          se.show_element === elementOrLevel || se.level === elementOrLevel
      );
      
      return result;
    };

    // Filter regular conformation options (single select)
    const regularConformationOptions = [
      {
        value: Constants.dog_class_element_level.akc_4_6_month_puppy,
        id: Constants.dog_class_element_level.akc_4_6_month_puppy,
      },
      {
        value:
          Constants.dog_class_element_level.akc_4_6_month_puppy_exhibitor_only,
        id: Constants.dog_class_element_level
          .akc_4_6_month_puppy_exhibitor_only,
      },
      {
        value: Constants.dog_class_element_level.akc_6_9_month_puppy,
        id: Constants.dog_class_element_level.akc_6_9_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_9_12_month_puppy,
        id: Constants.dog_class_element_level.akc_9_12_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_12_15_month_puppy,
        id: Constants.dog_class_element_level.akc_12_15_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_15_18_month_puppy,
        id: Constants.dog_class_element_level.akc_15_18_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_12_18_month_puppy,
        id: Constants.dog_class_element_level.akc_12_18_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_18_24_month_puppy,
        id: Constants.dog_class_element_level.akc_18_24_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_coursing,
        id: Constants.dog_class_element_level.akc_coursing,
      },
      {
        value:
          Constants.dog_class_element_level
            .akc_under_12_month_bred_by_exhibitor,
        id: Constants.dog_class_element_level
          .akc_under_12_month_bred_by_exhibitor,
      },
      {
        value: Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
        id: Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
      },
      {
        value: Constants.dog_class_element_level.akc_amateur_owner_handler,
        id: Constants.dog_class_element_level.akc_amateur_owner_handler,
      },
      {
        value: Constants.dog_class_element_level.akc_american_bred,
        id: Constants.dog_class_element_level.akc_american_bred,
      },
      {
        value: Constants.dog_class_element_level.akc_open,
        id: Constants.dog_class_element_level.akc_open,
      },
      {
        value: Constants.dog_class_element_level.akc_exhibition_only,
        id: Constants.dog_class_element_level.akc_exhibition_only,
      },
      {
        value: Constants.dog_class_element_level.akc_novice,
        id: Constants.dog_class_element_level.akc_novice,
      },
      {
        value: Constants.dog_class_element_level.akc_miscellaneous,
        id: Constants.dog_class_element_level.akc_miscellaneous,
      },
      {
        value: Constants.dog_class_element_level.akc_best_of_breed,
        id: Constants.dog_class_element_level.akc_best_of_breed,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_6_8_years,
        id: Constants.dog_class_element_level.akc_veteran_6_8_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_6_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_6_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_10_years,
        id: Constants.dog_class_element_level.akc_veteran_8_10_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_11_years,
        id: Constants.dog_class_element_level.akc_veteran_8_11_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_10_12_years,
        id: Constants.dog_class_element_level.akc_veteran_10_12_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_11_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_11_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_7_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_8_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_10_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_10_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_13_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_13_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_10_years,
        id: Constants.dog_class_element_level.akc_veteran_7_10_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_9_years,
        id: Constants.dog_class_element_level.akc_veteran_7_9_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_9_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_9_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_12_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_12_plus_years,
      },
    ].filter((option) => isElementAvailable(option.value));

    // Filter special conformation options (multi-select alongside regular)
    const specialConformationOptions = [
      {
        value: Constants.dog_class_element_level.akc_brood_bitch,
        id: Constants.dog_class_element_level.akc_brood_bitch,
      },
      {
        value: Constants.dog_class_element_level.akc_stud_dog,
        id: Constants.dog_class_element_level.akc_stud_dog,
      },
      {
        value: Constants.dog_class_element_level.akc_brace,
        id: Constants.dog_class_element_level.akc_brace,
      },
    ].filter((option) => isElementAvailable(option.value));

    // Filter non-licensed options based on available show elements
    const nonLicensedOptions = [
      // REMOVED: akc_puppy_sweepstakes is deprecated - use age-specific classes instead
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 6,
        maxAgeMonths: 9,
      },
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 9,
        maxAgeMonths: 12,
      },
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 12,
        maxAgeMonths: 18,
      },
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 12,
        maxAgeMonths: 15,
      },
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 15,
        maxAgeMonths: 18,
      },
      {
        value:
          Constants.dog_class_element_level
            .akc_puppy_sweepstakes_bred_by_exhibitor_6_18,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_bred_by_exhibitor_6_18,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 6,
        maxAgeMonths: 18,
      },
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
        requiresPuppyClass: true, // for validation
        minAgeMonths: 18,
        maxAgeMonths: 24,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_9,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_9,
        minAgeMonths: 84, // 7 years
        maxAgeMonths: 108, // 9 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        minAgeMonths: 84, // 7 years
        maxAgeMonths: 120, // 10 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
        minAgeMonths: 96, // 8 years
        maxAgeMonths: 120, // 10 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
        minAgeMonths: 96, // 8 years
        maxAgeMonths: 132, // 11 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_9_plus,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_9_plus,
        minAgeMonths: 108, // 9 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        minAgeMonths: 120, // 10 years
        maxAgeMonths: 144, // 12 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
        minAgeMonths: 132, // 11 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
        minAgeMonths: 120, // 10 years
        maxAgeMonths: 156, // 13 years
      },
      {
        value:
          Constants.dog_class_element_level.akc_veteran_sweepstakes_10_plus,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_plus,
        minAgeMonths: 120, // 10 years
      },
      {
        value:
          Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        minAgeMonths: 144, // 12 years
      },
      {
        value:
          Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,
        minAgeMonths: 156, // 13 years
      },
      {
        value:
          Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        id: Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        minAgeMonths: 6,
        maxAgeMonths: 84,
      },
      {
        value:
          Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
        id: Constants.dog_class_element_level
          .akc_dual_title_sweepstakes_7y_plus,
        minAgeMonths: 84,
        maxAgeMonths: 120,
      },
      {
        value: Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
        id: Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
        minAgeMonths: 84,
        maxAgeMonths: 132,
      },
      {
        value: Constants.dog_class_element_level.akc_dual_title_sweepstakes_10y_plus,
        id: Constants.dog_class_element_level.akc_dual_title_sweepstakes_10y_plus,
        minAgeMonths: 120,
      },
      {
        value: Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,
        id: Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,
        minAgeMonths: 132,
      },
      {
        value: Constants.dog_class_element_level.akc_futurity,
        id: Constants.dog_class_element_level.akc_futurity,
      },
      {
        value: Constants.dog_class_element_level.akc_sled_dog,
        id: Constants.dog_class_element_level.akc_sled_dog,
      },
      {
        value: Constants.dog_class_element_level.akc_hunting_class,
        id: Constants.dog_class_element_level.akc_hunting_class,
      },
      {
        value: Constants.dog_class_element_level.akc_field_trial_class,
        id: Constants.dog_class_element_level.akc_field_trial_class,
      },
      {
        value: Constants.dog_class_element_level.akc_teams,
        id: Constants.dog_class_element_level.akc_teams,
      },
      {
        value: Constants.dog_class_element_level.akc_parade_of_titleholders,
        id: Constants.dog_class_element_level.akc_parade_of_titleholders,
      },
      {
        value: Constants.dog_class_element_level.akc_best_head,
        id: Constants.dog_class_element_level.akc_best_head,
      },
      {
        value: Constants.dog_class_element_level.akc_best_movement,
        id: Constants.dog_class_element_level.akc_best_movement,
      },
      {
        value: Constants.dog_class_element_level.akc_best_field,
        id: Constants.dog_class_element_level.akc_best_field,
      },
    ].filter((option) => isElementAvailable(option.value));

    const formRows: IEventRegisterRow[] = [];

    // Add regular conformation section (single select - no duplicates)
    if (regularConformationOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation,
            multiSelect: true,
            options: regularConformationOptions,
          },
        ],
      });
    }

    // Add special conformation section (multi-select)
    if (specialConformationOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation_special_classes,
            multiSelect: true,
            options: specialConformationOptions,
          },
        ],
      });
    }

    // Only add non-licensed section if there are available options
    if (nonLicensedOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation_non_licensed,
            multiSelect: true,
            options: nonLicensedOptions,
          },
        ],
      });
    }

    return formRows;
  };
}
